import React, { useContext, useMemo, useRef, useState } from 'react';
import { InfiniteQueryResult, QueryKey } from 'react-query';
import { useTranslation } from 'react-i18next';
import { AgGridReactProps } from '@ag-grid-community/react';
import { GridApi } from '@ag-grid-community/core';
import UserContext from 'contexts/userContext';
import SharedRequestsDataGrid from 'fe-shared-requests/src/components/RequestsDataGrid';
import { Button, showNotification } from 'fe-shared/src/components';
import ExportExcelData from 'fe-shared/src/components/ExportExcelData';
import useDialog from 'fe-shared/src/hooks/useDialog';
import useTenantOptions from 'fe-shared/src/hooks/useTenantOptions';
import useRecycleMaterials from 'fe-shared/src/hooks/useRecycleMaterials';
import { PickupRequestsListResponse } from 'types';
import { PickupRequestDialog } from 'fe-shared-requests/src/components';
import { MATERIAL_ROLES } from 'fe-shared-requests/src/consts';
import getRequestParams from 'helpers/requestConverter';
import { CloseIcon } from 'fe-shared/src/components/Icons';
import {
  DataGridFilters,
  DataGridOptions,
} from 'fe-shared/src/components/DataGrid/modules/types';
import { hasPermission } from 'fe-shared/src/helpers/permissions';
import { PermissionLevel } from 'fe-shared/src/types/domain';
import { PERMISSIONS } from 'consts';

import * as requestsApi from 'routes/Requests/modules/api';
import RequestsAdditionalFilters from 'components/RequestsAdditionalFilters';

import {
  getColumnDefs,
  getColumnConfig,
  getRenderers,
  ASSIGNEE_COL_ID,
} from './modules/config';

interface Props extends AgGridReactProps {
  requestsQueryKey: QueryKey;
  requests: InfiniteQueryResult<PickupRequestsListResponse>;
  lastOpenedRequestId: string | null;
  clearLastOpenedRequestId: () => void;
  options: DataGridOptions;
  filters: DataGridFilters;
  setOptions: (options: DataGridOptions) => void;
  setFilters: (filters: DataGridFilters) => void;
}

const RequestsDataGrid = (props: Props) => {
  const { requestsQueryKey, filters, ...otherProps } = props;

  const { t } = useTranslation();
  const [tenantOptions] = useTenantOptions();
  const user = useContext(UserContext);
  const gridApiRef = useRef<GridApi | null>(null);
  const columnDefs = useMemo(() => getColumnDefs(t), [t]);
  const { data: recycleMaterials } = useRecycleMaterials(MATERIAL_ROLES);
  // recycleMaterials returns one row per material per role set, so a code repeats.
  const materialTypes = useMemo(
    () =>
      Array.from(
        new Map(
          (recycleMaterials ?? []).map((material) => [material.code, material] as const)
        ).values()
      ),
    [recycleMaterials]
  );
  const columnConfig = useMemo(
    () => getColumnConfig(t, tenantOptions.tenantName, materialTypes),
    [t, tenantOptions.tenantName, materialTypes]
  );

  const { isDialogActive, isDialogRendered, openDialog, closeDialog } =
    useDialog();

  const [dialogInstanceKey, setDialogInstanceKey] = useState(0);

  const canExport = hasPermission(user?.permissions, {
    name: PERMISSIONS.requestsListExport,
    level: PermissionLevel.Full,
  });

  const canAssign = hasPermission(user?.permissions, {
    name: PERMISSIONS.requestListAssign,
    level: PermissionLevel.Full,
  });

  const canAddRequest = hasPermission(user?.permissions, {
    name: PERMISSIONS.requestsListAddRequest,
    level: PermissionLevel.Full,
  });

  const canEditPriority = hasPermission(user?.permissions, {
    name: PERMISSIONS.requestListEditPriority,
    level: PermissionLevel.Full,
  });

  const renderers = useMemo(
    () => getRenderers(canAssign, canEditPriority),
    [canAssign, canEditPriority]
  );

  const onExportError = (error: any) => {
    showNotification(
      error.response?.data.message || error.message,
      undefined,
      CloseIcon
    );
  };

  // openDialog() only flips a flag - a close immediately followed by a reopen can leave
  // isDialogRendered continuously true, so the dialog never unmounts and the shared form's
  // validation state, seeded once on mount, carries over the previous values. Bumping this key
  // forces a fresh PickupRequestDialog instance on every opening.
  const onNewRequestButtonClicked = () => {
    setDialogInstanceKey((key) => key + 1);
    openDialog();
  };

  return (
    <>
      <SharedRequestsDataGrid
        title={t('request')}
        columnDefs={columnDefs}
        frameworkComponents={renderers}
        filters={filters}
        keyboardInteractiveColIds={[ASSIGNEE_COL_ID]}
        onGridApiReady={(api) => {
          gridApiRef.current = api;
        }}
        headerActionsSlot={(
          <>
            {canExport && recycleMaterials && (
              <ExportExcelData
                dataRows={[]}
                getDataRows={() =>
                  requestsApi
                    .getRequests(
                      'requests',
                      getRequestParams(filters, otherProps.options),
                      null
                    )
                    .then((res) => res.pickupRequests)}
                columnConfig={columnConfig}
                onError={onExportError}
                buttonId="export-request-button"
                fileName={t('pickupRequests.grid.export.fileName')}
                buttonLabel={t('pickupRequests.grid.export.buttonLabel')}
              />
            )}
            <RequestsAdditionalFilters
              filters={filters}
              updateFilters={(nextFilters) =>
                gridApiRef.current!.setFilterModel(nextFilters)}
            />
            {canAddRequest && (
              <Button
                id="open-new-request-dialog-btn"
                onClick={onNewRequestButtonClicked}
                size="medium"
              >
                {t('requests.addNew')}
              </Button>
            )}
          </>
        )}
        {...otherProps}
      />
      {isDialogRendered && (
        <PickupRequestDialog
          key={dialogInstanceKey}
          mode="create"
          requestsQueryKey={requestsQueryKey}
          isActive={isDialogActive}
          onClose={closeDialog}
        />
      )}
    </>
  );
};

export default RequestsDataGrid;
